<template>
  <div class = "license-container">

    <v-btn
      v-if   = "sessionsSummary"
      class  = "sessions-link"
      icon
      :color = "!isLicenseValid  || sessionsSummary.sessionsUsed === sessionsSummary.sessionsLimit ? 'error' : 'success'"
      :to    = "isAdmin ? '/sessions' : ''"
    >
      <v-icon>mdi-gauge</v-icon>
    </v-btn>

    <div
      v-if  = "sessionsSummary"
      class = "status-element mr-2"
    >
      <div class="label text-caption">Sessions</div>
      <div class="status">
        {{ isLicenseValid ? sessionsSummary.sessionsUsed : 0 }} / {{ isLicenseValid ? sessionsSummary.sessionsLimit : 0 }}
      </div>
    </div>

    <v-btn
      icon
      :color      = "isLicenseValid ? 'success' : 'error'"
      @click.stop = "$refs.license_dialog.show();"
    >
      <v-icon>mdi-key</v-icon>
    </v-btn>

    <div class = "status-element mr-2">
      <div class = "label text-caption">License</div>
      <div class = "status">{{ licenseState }}</div>
    </div>

    <v-dialog v-model    = "licenseReminder"
              transition = "dialog-top-transition"
              origin     = "center center"
              max-width  = "400"
    >
      <v-card>
        <v-card-title class = "headline">GLIM Subscription Renewal</v-card-title>
        <v-card-text>
          <div class = "margin-top-one">
            Your subscription will run out in <b>{{ daysToExpire }}</b> day<template v-if = "daysToExpire > 1">s</template>.
          </div>
        </v-card-text>
        <v-card-text>
          <div class = "margin-top-one">
            To renew please email <a href = "mailto:GLIM_Renewals@telestream.net">GLIM_Renewals@telestream.net</a> and
            request a renewal quotation for the following serial number.
          </div>
          <div class = "margin-top-one">SN: {{ licenseEntity }}</div>
          <v-checkbox
            v-if="daysToExpire <= 7"
            label   = "Ignore reminder for the rest of the day"
            v-model = "ignoreReminders"
            @change = "updateIgnoreReminders(ignoreReminders)"
          >
          </v-checkbox>
        </v-card-text>
      </v-card>
    </v-dialog>

    <LicenseDialog ref="license_dialog"></LicenseDialog>
  </div>
</template>

<script>

// #region Imports

/* vuex */
import { mapState } from 'vuex';

/* Telestream */
import { AllowedViews } from '../../services/allowed-views-service';
import { CHECK_LICENSE, FETCH_USER_INFO } from '@/utils/action-types';
import LicenseDialog from './LicenseDialog.vue';

// #endregion Imports
const LOCAL_DOMAINS       = ['localhost', '127.0.0.1'];

export default {
  name:       'License',
  components: {
    LicenseDialog
  },
  data: function () {
    return {
      dialog:                false,
      licenseInfo:           null,
      isLicenseValid:        false,
      licenseState:          null,
      activationKey:         null,
      licenseExpirationDate: null,
      licenseEntity:         null,
      licenseEntitlements:   null,
      licenseReminder:       false,
      daysToExpire:          null,
      sessionsSummary:       null,
      ignoreReminders:       false,
      AllowedViews
    };
  },
  computed: {
    ...mapState(['licenseService', 'uiAccess', 'user']),
    isAdmin() {
      return this.uiAccess.has(AllowedViews.Admin) || this.user.hasAccessTo.has(AllowedViews.Admin);
    },
  },
  async mounted() {
    this.$store.dispatch(CHECK_LICENSE);

    try {
      this.initUpdateHandler();
      await this.setLicenseState();
    } catch (err) {
      if (err.statusCode === 401) window.location.pathname = '/user/login';
      else throw err;
    }
  },
  methods: {
    async setLicenseState() {
      const response = await this.licenseService.getEntitlement();
      this.handleLicenseResponse(response);
    },
    initUpdateHandler() {
      this.licenseService.onLicenseUpdated((response) => {
        this.handleLicenseResponse(response);
      });

      this.licenseService.onNotifyLicenseExpiration(async (daysToExpire) => {
        await this.$store.dispatch(FETCH_USER_INFO);
        if (this.isAdmin || this.isLocalHost()) {
          this.daysToExpire = daysToExpire;
          this.licenseReminder = true;
        }
      });
      this.licenseService.onActiveSessionStatsChanged((newSessionsSummary) => {
        this.sessionsSummary = newSessionsSummary;
      });
    },
    handleLicenseResponse(response) {
      this.licenseState = response.licenseState;
      this.licenseEntitlements = undefined;
      if (!response.licenseState || !response.entitlementStates) {
        this.licenseState = 'No license';
        this.isLicenseValid = false;
      } else if (response.licenseState === 'Valid') {
        if ((response.entitlementStates['glimpse.pro'] === 'Enabled') || (response.entitlementStates['glimpse.standard'] === 'Enabled')) {
          this.isLicenseValid = true;

          for (var key in response.entitlementStates) {
            if (key === 'glimpse.standard') continue;
            if (key === 'glimpse.pro') continue;
            if (response.entitlementStates[key] === 'Enabled') {
              let display = key.replace('glimpse.', '');
              if (this.licenseEntitlements) {
                this.licenseEntitlements += ', ' + display;
              } else {
                this.licenseEntitlements = display;
              }
            }
          }
        } else if (response.entitlementStates['glimpse.standard'] === 'Expired') {
          this.isLicenseValid = false;
          this.licenseState = 'Expired';
        } else {
          this.isLicenseValid = false;
        }
      } else {
        this.isLicenseValid = false;
      }
      this.licenseEntity = response.entity;
      this.licenseInfo = response.description;
      this.licenseExpirationDate = response.expiryDate
        ? new Date(response.expiryDate).toLocaleString('en-US', {
          year:   'numeric',
          month:  'numeric',
          day:    'numeric',
          hour:   'numeric',
          minute: 'numeric',
          second: 'numeric',
        })
        : null;
    },
    async updateIgnoreReminders(ignoreReminders) {
      await this.licenseService.ignoreReminders(ignoreReminders);
    },
    isLocalHost() {
      return LOCAL_DOMAINS.includes(window.location.hostname);
    }
  },
};
</script>

<style lang = "scss" scoped>

@import '../../styles/colors.scss';

.license-container {
  display        : flex;
  flex-direction : row;

  // Puts a green halo around the button when on the sessions page.
  .sessions-link::before {
    opacity: 0 !important;
  }

  .status-element {
    display         : flex;
    flex-direction  : column;
    justify-content : center;

    .label {
      margin-bottom : -4px;
      color         : $darker-label-color;
    }

    .status {
      color : $label-color;
    }
  }
}

</style>
